// Skinning is served by TouchTab.Skin, not AccountManagement - hence this folder. The REST route is
// still /account-management/skins because PortalAPI kept its existing controller path when the service
// moved, so the portal contract never changed. Folder and route disagreeing is deliberate, not a leftover.
import { getJwt, HttpError } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;

export interface Skin {
    brandId: string;
    theme: string;
    logoOnLightMd5: string;
    logoOnDarkMd5: string;
    loyaltyLogoMd5: string;
    loyaltyPlaceholderMd5: string;
}

export interface SkinUpdate {
    theme?: string | null;
    logoOnLight?: File | null;
    logoOnDark?: File | null;
    loyaltyLogo?: File | null;
    loyaltyPlaceholder?: File | null;
    // Set to remove a currently-stored graphic (ignored for a slot that also has a replacement file).
    clearLogoOnLight?: boolean;
    clearLogoOnDark?: boolean;
    clearLoyaltyLogo?: boolean;
    clearLoyaltyPlaceholder?: boolean;
}

const readBody = async (response: Response) => {
    const contentType = response.headers.get('content-type') ?? '';
    return contentType.includes('json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');
};

export const skinApi = {
    get: async (brandId: string): Promise<Skin> => {
        const response = await fetch(`${BASE_URL}/account-management/skins/${brandId}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        const data = await readBody(response);
        if (!response.ok) throw new HttpError(response.status, data);
        return data as Skin;
    },
    update: async (brandId: string, update: SkinUpdate): Promise<void> => {
        // Only append the fields that changed; the server leaves omitted fields untouched.
        const form = new FormData();
        if (update.theme != null) form.append('theme', update.theme);
        if (update.logoOnLight) form.append('logoOnLight', update.logoOnLight);
        if (update.logoOnDark) form.append('logoOnDark', update.logoOnDark);
        if (update.loyaltyLogo) form.append('loyaltyLogo', update.loyaltyLogo);
        if (update.loyaltyPlaceholder) form.append('loyaltyPlaceholder', update.loyaltyPlaceholder);
        if (update.clearLogoOnLight) form.append('clearLogoOnLight', 'true');
        if (update.clearLogoOnDark) form.append('clearLogoOnDark', 'true');
        if (update.clearLoyaltyLogo) form.append('clearLoyaltyLogo', 'true');
        if (update.clearLoyaltyPlaceholder) form.append('clearLoyaltyPlaceholder', 'true');

        const response = await fetch(`${BASE_URL}/account-management/skins/${brandId}`, {
            method: 'PUT',
            body: form,
            // NOTE: don't set Content-Type — the browser adds the multipart boundary automatically.
            headers: { 'Authorization': `Bearer ${getJwt()}` },
        });
        if (!response.ok) throw new HttpError(response.status, await readBody(response));
    },
};
